// external libraries
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// types
import type { OpenRequestResponse } from 'RestAPIs/roster/open-requests/types';
import type { OpenRequestFunctionGroup } from '../RosterPlan.types';

// utils
import { groupOpenRequestsByFunction } from '../utils/groupOpenRequestsByFunction';

/**
 * The rows of the open requests grid: the filtered requests collapsed per function and status.
 *
 * The hook exists only to own `t` and the memo — the grouping itself is a pure util, so it can be
 * tested without a renderer.
 */
const useGroupedOpenRequests = (
  openRequests: OpenRequestResponse[]
): OpenRequestFunctionGroup[] => {
  const { t } = useTranslation('common', { keyPrefix: 'rosterPlan' });
  const unnamedFunctionLabel = t('dataGrid.functionWithNoGivenName');

  /**
   * Explicit `useMemo` and no React Compiler directive, against the wider repo's habit: this hook's
   * return identity is load bearing (it feeds `rows` on a DataGridPro), and the folder keeps an
   * explicit guarantee there rather than the compiler's best effort. Reasoning in README.md,
   * "Decisions".
   */
  return useMemo(
    () => groupOpenRequestsByFunction({ openRequests, unnamedFunctionLabel }),
    [openRequests, unnamedFunctionLabel]
  );
};

export default useGroupedOpenRequests;
